import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles, Mic, MicOff, Send, Target, X, Check, Undo2, Wand2, Loader2, History, Radio,
  Volume2, VolumeX, Dices, Paperclip, PenLine, Square, ArrowUpRight, Circle, Eraser,
  GitCompare, HelpCircle, Image as ImageIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import useSpeech from "@/lib/useSpeech";
import { toast } from "sonner";

const SURPRISES = [
  "make it rain candy", "give the hero a glowing jetpack", "turn the sky pink at golden sunset",
  "add a giant friendly dragon boss", "make everything bouncy and springy", "add floating islands in the sky",
  "make it a snowy night with fireflies", "give the hero a cute pet sidekick", "add a rainbow trail behind the hero",
  "make the world underwater with bubbles", "add a super speed-boost power-up", "make the enemies tiny and silly",
  "add a secret treasure chest", "make it a spooky haunted forest", "give the hero a magic double jump",
];

const WAKE_PHRASES = ["hey studio", "ok studio", "okay studio", "hey creator", "studio", "creator"];
function parseWakeCommand(transcript) {
  const t = (transcript || "").toLowerCase();
  for (const w of WAKE_PHRASES) {
    const idx = t.indexOf(w);
    if (idx >= 0) {
      const cmd = transcript.slice(idx + w.length).replace(/^[,:.\s]+/, "").trim();
      if (cmd.length >= 2) return cmd;
    }
  }
  return null;
}

// Safe AI Change Engine — the visible transaction pipeline stages.
const ENGINE_STAGES = ["Understand", "Plan", "Snapshot", "Sandbox", "Test", "Preview"];
const ANNOT_MODES = [
  ["box", Square, "Box (area)"], ["arrow", ArrowUpRight, "Arrow"], ["circle", Circle, "Circle"], ["free", PenLine, "Draw"],
];

// Resize a picked image to a compact JPEG data-URL so it posts cleanly to the vision edit.
function fileToDataUrl(file, max = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// GameView Live Creator 3.0 — floating in-play AI creation bar. Speak / type / select objects /
// draw on the screen / attach a reference image, preview the change live, then Accept / Refine /
// Compare / Explain / Undo (the Safe AI Change Engine — every accepted change is a traceable version).
export const LiveCreatorBar = ({ gameId, config, canvasRef, is3d = false, onPreview, onClear, onAccept, initialInstruction }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sandbox, setSandbox] = useState(null);   // pending, un-saved config
  const [changes, setChanges] = useState([]);
  const [summary, setSummary] = useState("");
  const [options, setOptions] = useState([]);      // clarify quick-replies
  const [pending, setPending] = useState("");      // instruction awaiting a clarify answer
  const [target, setTarget] = useState(null);      // {label,kind} from Point-and-Talk
  const [picking, setPicking] = useState(false);
  const [history, setHistory] = useState([]);
  const [note, setNote] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const [readback, setReadback] = useState(true);   // speak previews aloud (eyes-free)
  // Live Creator 3.0 additions
  const [attachedImage, setAttachedImage] = useState(null); // data-URL (reference or annotated shot)
  const [attachName, setAttachName] = useState("");
  const [area, setArea] = useState(null);           // normalized {x,y,w,h} drawn box
  const [annotating, setAnnotating] = useState(false);
  const [annotMode, setAnnotMode] = useState("box");
  const [stage, setStage] = useState(-1);           // change-engine pipeline stage index
  const [comparing, setComparing] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logEntries, setLogEntries] = useState(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const recogRef = useRef(null);
  const handsFreeRef = useRef(false);
  const sendRef = useRef(null);
  const acceptRef = useRef(null);
  const undoRef = useRef(null);
  const sandboxRef = useRef(null);
  const readbackRef = useRef(true);
  const lastInstrRef = useRef("");

  const { listening, supported: micSupported, start: micStart, stop: micStop } =
    useSpeech((t) => setText((p) => (p ? p + " " : "") + t));

  useEffect(() => { sandboxRef.current = sandbox; readbackRef.current = readback; });

  // Handoff from Code Brain / Game Director: open the bar with a prefilled instruction ready to send.
  useEffect(() => {
    if (initialInstruction && initialInstruction.trim()) {
      setOpen(true);
      setText(initialInstruction);
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [initialInstruction]);

  // Change-engine pipeline animation while an edit is running.
  useEffect(() => {
    if (!loading) { setStage(-1); return; }
    setStage(0);
    const iv = setInterval(() => setStage((s) => Math.min(ENGINE_STAGES.length - 2, s + 1)), 650);
    return () => clearInterval(iv);
  }, [loading]);

  const speak = (msg) => {
    try {
      const s = window.speechSynthesis;
      if (!s || !msg) return;
      s.cancel();
      const u = new SpeechSynthesisUtterance(String(msg));
      u.rate = 1.05; u.pitch = 1.05;
      s.speak(u);
    } catch {}
  };
  const shush = () => { try { window.speechSynthesis?.cancel(); } catch {} };

  const clearAttachment = () => { setAttachedImage(null); setAttachName(""); setArea(null); };

  // Point-and-Talk + visual annotation: the runner posts back picked entities / annotation captures.
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type === "gv-picked") {
        setPicking(false);
        if (d.entity && d.entity.label) {
          setTarget(d.entity);
          setOpen(true);
          toast.success(`🎯 Editing ${d.entity.label}`);
          setTimeout(() => inputRef.current?.focus(), 60);
        } else {
          toast.message("Nothing there — tap right on a character or object.");
        }
      } else if (d.type === "gv-annotation") {
        setAnnotating(false);
        if (d.image) {
          setAttachedImage(d.image);
          setArea(d.box || null);
          setAttachName(d.box ? "Area + drawing" : "Drawing");
          setOpen(true);
          toast.success(d.box ? "🖊️ Area selected — now say what to do here" : "🖊️ Drawing attached");
          setTimeout(() => inputRef.current?.focus(), 60);
        } else {
          toast.message("Couldn't capture the drawing — try again.");
        }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const stopPick = () => { setPicking(false); try { canvasRef?.current?.pickMode?.(false); } catch {} };

  const togglePick = () => {
    if (picking) { stopPick(); return; }
    setPicking(true);
    try { canvasRef?.current?.pickMode?.(true); } catch {}
    toast.message("🎯 Tap a character or object in the game");
  };

  // ── Visual annotation (draw box / arrow / circle / freehand over the live game) ──
  const startAnnotate = (mode = "box") => {
    setAnnotMode(mode); setAnnotating(true); setOpen(true);
    try { canvasRef?.current?.annotateStart?.(mode); } catch {}
    toast.message("🖊️ Draw on the game, then tap “Use drawing”");
  };
  const setDrawMode = (mode) => { setAnnotMode(mode); try { canvasRef?.current?.annotateMode?.(mode); } catch {} };
  const clearDrawing = () => { try { canvasRef?.current?.annotateClear?.(); } catch {} };
  const cancelAnnotate = () => { setAnnotating(false); try { canvasRef?.current?.annotateCancel?.(); } catch {} };
  const useDrawing = () => { try { canvasRef?.current?.annotateCapture?.(); } catch {} };

  const onAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Attach an image reference (PNG/JPG)."); e.target.value = ""; return; }
    try {
      const dataUrl = await fileToDataUrl(file);
      setAttachedImage(dataUrl); setAttachName(file.name.slice(0, 28)); setArea(null);
      toast.success("📎 Reference attached — describe what to do with it");
      setTimeout(() => inputRef.current?.focus(), 40);
    } catch { toast.error("Couldn't read that image."); }
    e.target.value = "";
  };

  const send = async (instruction) => {
    const instr = (instruction ?? text).trim();
    if (!instr || loading) return;
    setLoading(true);
    setOptions([]);
    setNote("");
    setExplanation(null);
    lastInstrRef.current = instr;
    try {
      const { data } = await api.post(`/creator/${gameId}/live-edit`, {
        instruction: instr,
        target: target?.label || undefined,
        base: sandbox || undefined,          // chained refine on top of the un-saved sandbox
        image: attachedImage || undefined,   // vision: reference / annotated screenshot
        area: area || undefined,             // scope the change to a drawn box
      }, { timeout: 90000 });
      if (data.clarify) {
        setSummary(data.summary || "Quick question — what did you have in mind?");
        setOptions(data.options || []);
        setPending(instr);
        return;
      }
      if (!data.changed) {
        const msg = "Nothing changed there — try naming the exact thing to tweak (like \u201cthe hero\u201d or \u201cthe enemy\u201d).";
        setSummary(msg);
        setNote(msg);
        toast.message(msg);
        return;
      }
      setNote("");
      setStage(ENGINE_STAGES.length - 1);
      setSandbox(data.config);
      setChanges(data.changes || []);
      setSummary(data.summary || "Done!");
      setPending("");
      setComparing(false);
      onPreview?.(data.config);
      if (readbackRef.current) {
        const base = (data.summary || "Change applied").replace(/[.!?\s]+$/, "");
        speak(base + ". Say accept, or undo.");
      }
      setHistory((h) => [{ instr, target: target?.label || "" }, ...h].slice(0, 8));
      setText("");
      clearAttachment();     // attachments are one-shot per request
      if (data.model_request) toast.message("🎨 Studio is sculpting a new 3D model — it'll pop in shortly.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Studio couldn't apply that live.");
    } finally { setLoading(false); }
  };
  sendRef.current = send;

  const toggleHandsFree = () => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) { toast.error("Voice isn't supported in this browser."); return; }
    if (handsFree) {
      handsFreeRef.current = false;
      try { recogRef.current?.stop(); } catch {}
      recogRef.current = null;
      setHandsFree(false);
      toast.message("🎙️ Hands-free off");
      return;
    }
    const r = new SR();
    r.continuous = true; r.interimResults = false; r.lang = "en-US";
    r.onresult = (e) => {
      const txt = e.results?.[e.results.length - 1]?.[0]?.transcript || "";
      const low = txt.toLowerCase();
      if (sandboxRef.current) {
        if (/\b(accept|yes|keep it|keep|save it|confirm|looks good|perfect)\b/.test(low)) { toast.message("🎙️ Accept"); acceptRef.current?.(); return; }
        if (/\b(undo|no|revert|cancel|nope|remove it|go back)\b/.test(low)) { toast.message("🎙️ Undo"); undoRef.current?.(); return; }
      }
      const cmd = parseWakeCommand(txt);
      if (cmd) { setOpen(true); toast.message(`🎙️ “${cmd}”`); sendRef.current?.(cmd); }
    };
    r.onerror = () => {};
    r.onend = () => { if (handsFreeRef.current && recogRef.current === r) { try { r.start(); } catch {} } };
    recogRef.current = r;
    handsFreeRef.current = true;
    try { r.start(); setHandsFree(true); toast.success('🎙️ Hands-free on — say "Hey Studio, make it snowy"'); }
    catch { handsFreeRef.current = false; recogRef.current = null; toast.error("Couldn't start voice."); }
  };

  useEffect(() => () => { handsFreeRef.current = false; try { recogRef.current?.stop(); } catch {} shush(); }, []);

  const chooseOption = (opt) => {
    const merged = pending ? `${pending} — ${opt}` : opt;
    setOptions([]);
    send(merged);
  };

  const accept = async () => {
    if (!sandbox) return;
    shush();
    setAccepting(true);
    try {
      const { data } = await api.post(`/creator/${gameId}/live-accept`, {
        config: sandbox,
        summary: summary || "Live edit",
        instruction: lastInstrRef.current || "",
        changes,
      });
      onAccept?.(data.config);
      setSandbox(null); setChanges([]); setSummary(""); setComparing(false); setExplanation(null);
      if (logEntries) loadLog();
      toast.success(`✅ Saved${data.version ? ` — version ${data.version}` : ""}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't save that change.");
    } finally { setAccepting(false); }
  };

  const undo = () => {
    shush();
    setSandbox(null); setChanges([]); setSummary(""); setOptions([]); setPending(""); setNote("");
    setComparing(false); setExplanation(null);
    onClear?.();
    toast.message("↩︎ Reverted to the live game");
  };
  acceptRef.current = accept;
  undoRef.current = undo;

  const toggleCompare = () => {
    if (!sandbox) return;
    const c = !comparing;
    setComparing(c);
    onPreview?.(c ? config : sandbox);   // Before ↔ After live in the game
  };

  const doExplain = async () => {
    if (!sandbox || explaining) return;
    setExplaining(true);
    try {
      const { data } = await api.post(`/creator/${gameId}/live-explain`, {
        before: config, after: sandbox, instruction: lastInstrRef.current || summary || "the change",
      }, { timeout: 60000 });
      setExplanation(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't explain that.");
    } finally { setExplaining(false); }
  };

  const loadLog = async () => {
    try { const { data } = await api.get(`/creator/${gameId}/change-log`); setLogEntries(data.entries || []); }
    catch { setLogEntries([]); }
  };
  const toggleLog = () => {
    const s = !showLog;
    setShowLog(s);
    if (s && logEntries === null) loadLog();
  };

  const surpriseMe = () => {
    const s = SURPRISES[Math.floor(Math.random() * SURPRISES.length)];
    setText(s);
    send(s);
  };

  const closeBar = () => {
    setOpen(false); stopPick(); cancelAnnotate();
    if (sandbox) undo();
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 60); }}
        data-testid="live-creator-fab"
        className={`absolute bottom-5 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-[0_10px_30px_rgba(0,0,0,0.45)] transition-all active:scale-95 ${handsFree ? "bg-gradient-to-r from-rose-500 to-fuchsia-500" : "bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] hover:opacity-95"}`}
      >
        {handsFree
          ? <><span className="w-2.5 h-2.5 rounded-full bg-white animate-ping" data-testid="live-creator-fab-listening" /> Listening — “Hey Studio…”</>
          : <><Sparkles className="w-4 h-4" /> ✨ Create</>}
      </button>
    );
  }

  return (
    <div
      data-testid="live-creator-bar"
      className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 w-[min(94%,560px)] rounded-2xl border border-white/15 bg-[#0d1220]/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-3 text-white"
    >
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAttachFile} data-testid="live-creator-file" />
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#c4b5fd]">
          <Sparkles className="w-3.5 h-3.5" /> Live Creator
        </span>
        {target && (
          <span data-testid="live-creator-target" className="inline-flex items-center gap-1 rounded-full bg-[#3b9eff]/20 border border-[#3b9eff]/50 px-2 py-0.5 text-[11px] font-semibold text-[#93c5fd] max-w-[45%] truncate">
            <Target className="w-3 h-3 shrink-0" /> Editing: {target.label}
            <button onClick={() => setTarget(null)} data-testid="live-creator-clear-target" className="ml-0.5 hover:text-white"><X className="w-3 h-3" /></button>
          </span>
        )}
        <button onClick={toggleLog} data-testid="live-creator-log-toggle" title="Change history"
          className={`ml-auto grid place-items-center w-7 h-7 rounded-full border transition-colors ${showLog ? "bg-white/15 border-white/30 text-white" : "border-white/15 text-white/55 hover:bg-white/10"}`}>
          <History className="w-3.5 h-3.5" />
        </button>
        <button onClick={closeBar} data-testid="live-creator-close" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-1.5 mb-2 -mt-1">
        <button onClick={toggleHandsFree} data-testid="live-creator-handsfree"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors ${handsFree ? "bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse" : "border-white/15 text-white/55 hover:bg-white/10"}`}>
          <Radio className="w-3 h-3" /> {handsFree ? "Listening — say “Hey Studio…”" : "Hands-free"}
        </button>
        <button onClick={() => { const v = !readback; setReadback(v); if (!v) shush(); }} data-testid="live-creator-readback"
          title={readback ? "Voice confirm on — I'll read changes aloud" : "Voice confirm off"}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors ${readback ? "bg-[#3b9eff]/15 border-[#3b9eff]/50 text-[#93c5fd]" : "border-white/15 text-white/45 hover:bg-white/10"}`}>
          {readback ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />} Voice confirm
        </button>
      </div>

      {/* Change history (traceability) */}
      {showLog && (
        <div data-testid="live-creator-log" className="mb-2 rounded-xl border border-white/12 bg-white/5 p-2.5 max-h-40 overflow-y-auto">
          <p className="text-[11px] font-bold text-white/70 mb-1.5">Change history</p>
          {logEntries === null ? (
            <p className="text-[11px] text-white/40">Loading…</p>
          ) : logEntries.length === 0 ? (
            <p className="text-[11px] text-white/40" data-testid="live-creator-log-empty">No saved changes yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {logEntries.map((en, i) => (
                <li key={en.id || i} data-testid={`live-creator-log-${i}`} className="text-[11px] text-white/70">
                  <span className="text-white/90 font-medium">v{en.version}</span> · {en.summary}
                  {en.changes?.length > 0 && <span className="text-white/45"> — {en.changes.length} item{en.changes.length === 1 ? "" : "s"}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Change-engine pipeline (Safe AI Change Engine) */}
      {loading && (
        <div data-testid="change-engine-pipeline" className="mb-2 rounded-xl border border-[#7c3aed]/40 bg-[#7c3aed]/10 p-2.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {ENGINE_STAGES.map((s, i) => (
              <span key={s} data-testid={`engine-stage-${s.toLowerCase()}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${i < stage ? "bg-emerald-500/20 text-emerald-300" : i === stage ? "bg-[#7c3aed]/40 text-white animate-pulse" : "bg-white/5 text-white/35"}`}>
                {i < stage ? <Check className="w-2.5 h-2.5" /> : i === stage ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}{s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Change preview (transaction summary) */}
      {sandbox && !loading && (
        <div data-testid="live-creator-changes" className="mb-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-2.5">
          <p className="text-[12px] font-bold text-emerald-300 mb-1.5">
            ✨ GameView changed {changes.length || 1} {changes.length === 1 ? "item" : "items"}
          </p>
          {summary && <p className="text-[11px] text-white/70 mb-1.5">{summary}</p>}
          {changes.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mb-2">
              {changes.map((c, i) => (
                <li key={i} data-testid={`live-creator-change-${i}`} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/85">{c}</li>
              ))}
            </ul>
          )}
          {explanation && (
            <div data-testid="live-creator-explanation" className="mb-2 rounded-lg bg-black/25 border border-white/10 p-2">
              <p className="text-[11px] text-white/80">{explanation.explanation}</p>
              {explanation.bullets?.length > 0 && (
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {explanation.bullets.map((b, i) => <li key={i} className="text-[10px] text-white/55">{b}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={accept} disabled={accepting} data-testid="live-creator-accept" className="h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
              {accepting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accept
            </Button>
            <Button size="sm" variant="outline" onClick={toggleCompare} data-testid="live-creator-compare"
              className={`h-8 rounded-full border-white/20 bg-transparent text-white text-xs hover:bg-white/10 ${comparing ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : ""}`}>
              <GitCompare className="w-3.5 h-3.5" /> {comparing ? "Showing BEFORE" : "Compare"}
            </Button>
            <Button size="sm" variant="outline" onClick={doExplain} disabled={explaining} data-testid="live-creator-explain"
              className="h-8 rounded-full border-white/20 bg-transparent text-white text-xs hover:bg-white/10">
              {explaining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HelpCircle className="w-3.5 h-3.5" />} Explain
            </Button>
            <Button size="sm" variant="outline" onClick={undo} data-testid="live-creator-undo" className="h-8 rounded-full border-white/20 bg-transparent text-white text-xs hover:bg-white/10">
              <Undo2 className="w-3.5 h-3.5" /> Undo
            </Button>
            <span className="ml-auto text-[10px] text-white/45">Keep typing to refine</span>
          </div>
        </div>
      )}

      {/* Clarify options */}
      {options.length > 0 && (
        <div className="mb-2">
          {summary && <p className="text-[12px] text-white/80 mb-1.5">{summary}</p>}
          <div className="flex flex-wrap gap-1.5">
            {options.map((o, i) => (
              <button key={i} onClick={() => chooseOption(o)} data-testid={`live-creator-option-${i}`}
                className="rounded-full border border-[#7c3aed]/50 bg-[#7c3aed]/15 px-2.5 py-1 text-[11px] font-semibold text-[#c4b5fd] hover:bg-[#7c3aed]/30">
                {o}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No-change / info note */}
      {note && !sandbox && options.length === 0 && (
        <div data-testid="live-creator-note" className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200">
          {note}
        </div>
      )}

      {/* Annotation toolbar (draw box / arrow / circle / freehand on the live game) */}
      {annotating && (
        <div data-testid="live-creator-annot-toolbar" className="mb-2 rounded-xl border border-[#ff3b6b]/40 bg-[#ff3b6b]/10 p-2.5">
          <p className="text-[11px] font-bold text-[#ff9db3] mb-1.5">🖊️ Draw on the game to point at what you mean</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            {ANNOT_MODES.map(([m, Icon, label]) => (
              <button key={m} onClick={() => setDrawMode(m)} data-testid={`live-creator-annot-${m}`} title={label}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border transition-colors ${annotMode === m ? "bg-[#ff3b6b] border-[#ff3b6b] text-white" : "border-white/15 text-white/70 hover:bg-white/10"}`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
            <button onClick={clearDrawing} data-testid="live-creator-annot-clear" className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border border-white/15 text-white/60 hover:bg-white/10">
              <Eraser className="w-3 h-3" /> Clear
            </button>
            <Button size="sm" onClick={useDrawing} data-testid="live-creator-annot-use" className="ml-auto h-7 rounded-full bg-[#ff3b6b] hover:bg-[#e02e5b] text-white text-[11px]">
              <Check className="w-3 h-3" /> Use drawing
            </Button>
            <button onClick={cancelAnnotate} data-testid="live-creator-annot-cancel" className="text-[10px] text-white/50 hover:text-white px-1">Cancel</button>
          </div>
        </div>
      )}

      {/* Attachment chip (reference image / captured drawing) */}
      {attachedImage && (
        <div data-testid="live-creator-attachment" className="mb-2 flex items-center gap-2 rounded-xl border border-[#3b9eff]/40 bg-[#3b9eff]/10 p-2">
          <img src={attachedImage} alt="attachment" className="w-10 h-10 rounded-md object-cover border border-white/15" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-[#93c5fd] truncate">{attachName || "Reference"}</p>
            {area && <p className="text-[10px] text-white/45" data-testid="live-creator-area-note">Area selected — change applies here</p>}
          </div>
          <button onClick={clearAttachment} data-testid="live-creator-attachment-clear" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Command input */}
      <div className="flex items-center gap-1.5">
        <button onClick={surpriseMe} data-testid="live-creator-surprise" title="Surprise me — apply a fun random change"
          disabled={loading}
          className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors">
          <Dices className="w-4 h-4" />
        </button>
        {is3d && (
          <button onClick={togglePick} data-testid="live-creator-pick" title="Point & Talk — tap an object to edit it"
            className={`shrink-0 grid place-items-center w-9 h-9 rounded-full border transition-colors ${picking ? "bg-[#3b9eff] border-[#3b9eff] text-white animate-pulse" : "border-white/20 text-white/80 hover:bg-white/10"}`}>
            <Target className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => (annotating ? cancelAnnotate() : startAnnotate("box"))} data-testid="live-creator-annotate" title="Draw / select an area on the game"
          className={`shrink-0 grid place-items-center w-9 h-9 rounded-full border transition-colors ${annotating ? "bg-[#ff3b6b] border-[#ff3b6b] text-white animate-pulse" : "border-white/20 text-white/80 hover:bg-white/10"}`}>
          <PenLine className="w-4 h-4" />
        </button>
        <button onClick={() => fileRef.current?.click()} data-testid="live-creator-attach" title="Attach a reference image"
          className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-white/20 text-white/80 hover:bg-white/10 transition-colors">
          <Paperclip className="w-4 h-4" />
        </button>
        <div className="flex-1 flex items-center gap-1.5 rounded-full bg-white/8 border border-white/12 px-2">
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder={target ? `Change ${target.label}…` : area ? "What should happen in this area?…" : "Tell Studio what to change…"}
            data-testid="live-creator-input"
            disabled={loading}
            className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-white/40 outline-none"
          />
          {micSupported && (
            <button onClick={() => (listening ? micStop() : micStart())} data-testid="live-creator-mic"
              className={`shrink-0 grid place-items-center w-8 h-8 rounded-full ${listening ? "text-rose-400 animate-pulse" : "text-white/60 hover:text-white"}`}>
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
        </div>
        <Button size="icon" onClick={() => send()} disabled={loading || !text.trim()} data-testid="live-creator-send"
          className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] text-white hover:opacity-90">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>

      {/* Quick suggestions + history */}
      {!sandbox && options.length === 0 && !annotating && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(is3d
            ? ["make the hero jump higher", "make it snowy at night", "add a boss", "make it cinematic"]
            : ["make the hero faster", "add more coins", "make it harder", "add fireflies"]
          ).map((s, i) => (
            <button key={i} onClick={() => send(s)} data-testid={`live-creator-suggest-${i}`}
              className="rounded-full bg-white/8 border border-white/10 px-2.5 py-1 text-[10px] text-white/70 hover:bg-white/15">
              <Wand2 className="w-3 h-3 inline mr-1 -mt-0.5" />{s}
            </button>
          ))}
        </div>
      )}
      {history.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <History className="w-3 h-3 text-white/40 shrink-0" />
          {history.map((h, i) => (
            <button key={i} onClick={() => setText(h.instr)} data-testid={`live-creator-history-${i}`}
              className="shrink-0 rounded-full bg-white/6 px-2 py-0.5 text-[10px] text-white/55 hover:bg-white/12 truncate max-w-[140px]">
              {h.target ? `🎯 ${h.instr}` : h.instr}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveCreatorBar;
